import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemContent,
  Button,
  CircularProgress,
  Alert,
  Breadcrumbs,
  Link,
  IconButton,
  Stack,
  Divider
} from '@mui/joy';
import { FaFolder, FaArrowUp, FaChevronRight } from 'react-icons/fa';
import { IconWrapper } from './IconWrapper';
import { listOneDriveFolders, getFolderPath, type DriveItem } from '../utils/graphService';

interface FolderBrowserProps {
  onSelectFolder: (folder: DriveItem) => void;
  onCancel: () => void;
}

const FolderBrowser: React.FC<FolderBrowserProps> = ({ onSelectFolder, onCancel }) => {
  const [currentFolderId, setCurrentFolderId] = useState<string | undefined>();
  const [folders, setFolders] = useState<DriveItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [path, setPath] = useState<DriveItem[]>([]);

  const loadFolders = async (folderId?: string) => {
    try {
      setLoading(true);
      setError(null);
      const items = await listOneDriveFolders(folderId);
      setFolders(items);
      
      if (folderId) {
        const folderPath = await getFolderPath(folderId);
        setPath(folderPath);
      } else {
        setPath([]);
      }
    } catch (err) {
      console.error('Error loading folders:', err);
      setError('Failed to load folders. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFolders(currentFolderId);
  }, [currentFolderId]);

  const handleFolderClick = (folder: DriveItem) => {
    setCurrentFolderId(folder.id);
  };

  const handlePathClick = (folder: DriveItem) => {
    setCurrentFolderId(folder.id);
  };

  const handleUpClick = () => {
    if (path.length > 1) {
      // Go to parent folder
      const parentFolder = path[path.length - 2];
      setCurrentFolderId(parentFolder.id);
    } else {
      // Go to root
      setCurrentFolderId(undefined);
    }
  };

  return (
    <Box sx={{ width: '100%', maxHeight: '60vh', display: 'flex', flexDirection: 'column' }}>
      {/* Breadcrumb navigation */}
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Button
          variant="plain"
          size="sm"
          startDecorator={<IconWrapper icon={FaArrowUp} />}
          onClick={handleUpClick}
          disabled={path.length <= 1}
        >
          Up
        </Button>
        <Breadcrumbs
          separator={<IconWrapper icon={FaChevronRight} />}
          sx={{ flex: 1, minWidth: 0 }}
        >
          <Link
            component="button"
            onClick={() => setCurrentFolderId(undefined)}
            sx={{ color: 'neutral.500' }}
          >
            OneDrive
          </Link>
          {path.map((folder, index) => (
            <Link
              key={folder.id}
              component="button"
              onClick={() => handlePathClick(folder)}
              sx={{ color: index === path.length - 1 ? 'primary.500' : 'neutral.500' }}
            >
              {folder.name}
            </Link>
          ))}
        </Breadcrumbs>
      </Box>

      {error && (
        <Alert color="danger" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Folder list */}
      <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : folders.length === 0 ? (
          <Box sx={{ textAlign: 'center', p: 3, color: 'neutral.500' }}>
            <Typography level="body-md">No folders found</Typography>
          </Box>
        ) : (
          <List>
            {folders.map((folder) => (
              <React.Fragment key={folder.id}>
                <ListItem
                  sx={{
                    cursor: 'pointer',
                    '&:hover': {
                      bgcolor: 'background.level1',
                    },
                  }}
                  onClick={() => handleFolderClick(folder)}
                >
                  <ListItemContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <IconWrapper icon={FaFolder} color="warning.500" />
                      <Typography level="body-md">{folder.name}</Typography>
                    </Box>
                  </ListItemContent>
                </ListItem>
                <Divider />
              </React.Fragment>
            ))}
          </List>
        )}
      </Box>

      {/* Action buttons */}
      <Stack direction="row" spacing={1} sx={{ mt: 2, justifyContent: 'flex-end' }}>
        <Button
          variant="outlined"
          color="neutral"
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button
          variant="solid"
          color="primary"
          onClick={() => path.length > 0 && onSelectFolder(path[path.length - 1])}
          disabled={path.length === 0}
        >
          Select Current Folder
        </Button>
      </Stack>
    </Box>
  );
};

export default FolderBrowser; 